import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { ISpClaimedReward, ISpRewardsSummary, RedeemSpRewardInput } from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import type { ApiResponse, PaginationMeta } from '@/api/http/types'
import { queryKeys } from '@/api/query-keys'
import { runDetached } from '@/shared/lib/runDetachedPromise'

type ClaimedRewardsQueryData = {
  items: ISpClaimedReward[]
  meta: PaginationMeta | null
}

/** BFP-495 F2 — seed claimed list from redeem response before background refetch completes. */
function hydrateClaimedCache(qc: QueryClient, claimed: ISpClaimedReward): void {
  qc.setQueriesData<ClaimedRewardsQueryData>(
    { queryKey: ['sp', 'rewards', 'claimed'] },
    (old) => {
      if (!old) return old
      const exists = old.items.some(
        (row) => row.id === claimed.id || row.transactionId === claimed.transactionId,
      )
      if (exists) return old
      const total = (old.meta?.total ?? old.items.length) + 1
      const perPage = old.meta?.per_page ?? 20
      return {
        items: [claimed, ...old.items],
        meta: old.meta
          ? { ...old.meta, total, total_pages: Math.max(1, Math.ceil(total / perPage)) }
          : { total, page: 1, per_page: perPage, total_pages: 1 },
      }
    },
  )
}

/** BFP-495 INV-2/INV-4 — reflect post-debit balance until summary refetch lands. */
function hydrateSummaryBalance(qc: QueryClient, claimed: ISpClaimedReward): void {
  qc.setQueryData<ISpRewardsSummary | undefined>(queryKeys.spRewards.summary(), (old) => {
    if (!old) return old
    return {
      ...old,
      availableBalanceUsd: Math.max(0, old.availableBalanceUsd - claimed.amountUsd),
    }
  })
}

export type RedeemSpRewardArgs = RedeemSpRewardInput & {
  /** Reuse when retrying the same redemption after a network error. */
  idempotencyKey?: string
}

export async function redeemSpReward(
  input: RedeemSpRewardArgs,
): Promise<ISpClaimedReward> {
  const idempotencyKey = input.idempotencyKey ?? globalThis.crypto.randomUUID()
  const { productId, amountUsd } = input
  const { data } = await apiClient.post<ApiResponse<ISpClaimedReward>>(
    '/api/v1/sp/rewards/redeem',
    { productId, amountUsd },
    {
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    },
  )
  if (!data.data) {
    throw new Error('Redeem response missing data')
  }
  return data.data
}

export function useRedeemSpReward() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: redeemSpReward,
    onSuccess: (claimed) => {
      hydrateClaimedCache(qc, claimed)
      hydrateSummaryBalance(qc, claimed)
      runDetached(qc.invalidateQueries({ queryKey: queryKeys.spRewards.summary() }))
      runDetached(qc.invalidateQueries({ queryKey: ['sp', 'rewards', 'claimed'] }))
      runDetached(qc.invalidateQueries({ queryKey: ['sp', 'rewards', 'brands'] }))
    },
  })
}
